// HM-374 cluster 16.4: CamereSection refactor con CRUD completo.
// Mantiene dropdown tipologia inline + aggiunge bottone Aggiungi +
// modal create/edit (numero, edificio dropdown, piano, tipologia) +
// soft delete con confirmation.

import { toast } from '@hotel-manager/ui/components';
import { Pencil, Plus, Trash2, X } from '@hotel-manager/ui/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { listBuildings } from '../../../api/buildings.js';
import { ApiError, api } from '../../../api/client.js';
import { listRoomTypesFull } from '../../../api/pricing.js';
import { type RoomDb, fetchRooms } from '../../../api/rooms.js';
import { buildLabelMap, buildingLabelOf } from '../../../lib/buildingLabel.js';

async function patchRoom(id: string, body: Record<string, unknown>) {
  return api(`/rooms/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}
async function createRoom(body: Record<string, unknown>) {
  return api('/rooms', { method: 'POST', body: JSON.stringify(body) });
}
async function deleteRoom(id: string) {
  return api(`/rooms/${id}`, { method: 'DELETE', body: '{}' });
}

export function CamereSection() {
  const qc = useQueryClient();
  const roomsQuery = useQuery({ queryKey: ['rooms'], queryFn: fetchRooms });
  const typesQuery = useQuery({ queryKey: ['room-types'], queryFn: listRoomTypesFull });
  const buildingsQuery = useQuery({ queryKey: ['buildings'], queryFn: listBuildings });
  const [editing, setEditing] = useState<RoomDb | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RoomDb | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const updateTypeMutation = useMutation({
    mutationFn: ({ id, roomTypeId }: { id: string; roomTypeId: string }) =>
      patchRoom(id, { roomTypeId }),
    onSuccess: () => {
      toast.success('Camera aggiornata');
      qc.invalidateQueries({ queryKey: ['rooms'] });
    },
    onError: () => toast.error('Errore aggiornamento camera'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRoom(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rooms'] });
      setDeleteTarget(null);
      setErrMsg(null);
    },
    onError: (err: unknown) => setErrMsg(formatErr(err)),
  });

  if (roomsQuery.isLoading || typesQuery.isLoading) {
    return <p className="text-text-secondary">Caricamento…</p>;
  }

  const rooms = (roomsQuery.data?.rooms ?? []).sort((a, b) => a.code.localeCompare(b.code));
  const types = typesQuery.data?.roomTypes ?? [];
  const buildings = buildingsQuery.data?.buildings ?? [];
  const labelMap = buildLabelMap(buildings);

  return (
    <section data-testid="camere-section" className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-text-primary">Camere</h2>
          <p className="text-xs text-text-secondary">
            Gestisci le camere dell'hotel; assegna tipologia ed edificio.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          data-testid="camera-add"
          className="bg-accent-primary text-text-on-accent px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 hover:bg-accent-primary-hover"
        >
          <Plus size={15} /> Aggiungi camera
        </button>
      </header>

      <div className="bg-bg-card border border-border-base rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-base text-text-secondary text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Camera</th>
              <th className="text-left px-4 py-3 font-semibold">Edificio</th>
              <th className="text-left px-4 py-3 font-semibold">Piano</th>
              <th className="text-left px-4 py-3 font-semibold">Tipologia</th>
              <th className="text-left px-4 py-3 font-semibold">Stato</th>
              <th className="px-4 py-3 font-semibold text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {rooms.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-text-muted">
                  Nessuna camera. Aggiungi la prima.
                </td>
              </tr>
            )}
            {rooms.map((room) => (
              <tr
                key={room.id}
                className="border-t border-border-base"
                data-testid={`row-room-${room.code}`}
              >
                <td className="px-4 py-3 font-mono font-bold text-text-primary">{room.code}</td>
                <td className="px-4 py-3 text-text-secondary">
                  {buildingLabelOf(room.building, labelMap)}
                </td>
                <td className="px-4 py-3 text-text-secondary">{room.floor}</td>
                <td className="px-4 py-3">
                  <select
                    className="border border-border-input rounded-md px-2.5 py-1.5 text-sm bg-bg-card outline-none focus:border-accent-primary min-w-[140px]"
                    value={room.roomTypeId}
                    onChange={(e) =>
                      updateTypeMutation.mutate({ id: room.id, roomTypeId: e.target.value })
                    }
                    data-testid={`type-select-${room.code}`}
                  >
                    {types.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-text-muted">{room.status}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditing(room)}
                      data-testid={`camera-edit-${room.code}`}
                      className="p-1.5 rounded-md hover:bg-bg-base text-text-secondary"
                      aria-label="Modifica"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(room)}
                      data-testid={`camera-delete-${room.code}`}
                      className="p-1.5 rounded-md hover:bg-[#FAEAE8] text-text-secondary hover:text-[#A8453A]"
                      aria-label="Elimina"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <RoomEditModal
          target={editing === 'new' ? null : editing}
          types={types}
          buildings={buildings}
          onClose={() => {
            setEditing(null);
            setErrMsg(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['rooms'] });
            setEditing(null);
            setErrMsg(null);
          }}
          errMsg={errMsg}
          setErrMsg={setErrMsg}
        />
      )}

      {deleteTarget && (
        <RoomDeleteConfirm
          room={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          loading={deleteMutation.isPending}
          errMsg={errMsg}
        />
      )}
    </section>
  );
}

// --------------------------- RoomEditModal ---------------------------

function RoomEditModal({
  target,
  types,
  buildings,
  onClose,
  onSaved,
  errMsg,
  setErrMsg,
}: {
  target: RoomDb | null;
  types: Array<{ id: string; label: string }>;
  buildings: Array<{ id: string; code: string; label: string }>;
  onClose: () => void;
  onSaved: () => void;
  errMsg: string | null;
  setErrMsg: (m: string | null) => void;
}) {
  const isNew = target === null;
  const [code, setCode] = useState(target?.code ?? '');
  const [floor, setFloor] = useState(target?.floor ?? '1');
  // Resolve initial buildingId from target.building text via buildings list.
  const initBuildingId = target
    ? (buildings.find((b) => b.code === target.building)?.id ?? '')
    : '';
  const [buildingId, setBuildingId] = useState(initBuildingId);
  const [roomTypeId, setRoomTypeId] = useState(target?.roomTypeId ?? types[0]?.id ?? '');

  const mutation = useMutation({
    mutationFn: () => {
      const buildingCode = buildings.find((b) => b.id === buildingId)?.code ?? '';
      const payload = {
        code,
        floor,
        building: buildingCode, // legacy text field per backward compat
        buildingId: buildingId || null,
        roomTypeId,
      };
      if (isNew) return createRoom(payload);
      return patchRoom(target.id, payload);
    },
    onSuccess: onSaved,
    onError: (err: unknown) => setErrMsg(formatErr(err)),
  });

  return (
    <ModalShell
      title={isNew ? 'Nuova camera' : `Modifica camera ${target?.code}`}
      onClose={onClose}
    >
      <div className="px-6 py-5 flex flex-col gap-4">
        <Field
          label="Numero"
          required
          value={code}
          onChange={setCode}
          placeholder="es. 101"
          testId="camera-code"
        />
        <SelectField
          label="Edificio"
          required
          value={buildingId}
          onChange={setBuildingId}
          options={buildings.map((b) => ({ value: b.id, label: b.label }))}
          testId="camera-building"
        />
        <Field
          label="Piano"
          required
          value={floor}
          onChange={setFloor}
          type="text"
          placeholder="es. 1, P1"
          testId="camera-floor"
        />
        <SelectField
          label="Tipologia"
          required
          value={roomTypeId}
          onChange={setRoomTypeId}
          options={types.map((t) => ({ value: t.id, label: t.label }))}
          testId="camera-type"
        />
        {errMsg && (
          <p role="alert" className="text-xs text-red-600">
            {errMsg}
          </p>
        )}
      </div>
      <ModalFooter
        onCancel={onClose}
        onConfirm={() => mutation.mutate()}
        loading={mutation.isPending}
      />
    </ModalShell>
  );
}

// --------------------------- RoomDeleteConfirm ---------------------------

function RoomDeleteConfirm({
  room,
  onCancel,
  onConfirm,
  loading,
  errMsg,
}: {
  room: RoomDb;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  errMsg: string | null;
}) {
  return (
    <ModalShell title="Eliminare camera?" onClose={onCancel}>
      <div className="px-6 py-5 flex flex-col gap-3">
        <p className="text-sm text-text-primary">
          Eliminare la camera <strong>{room.code}</strong> ({room.building} · piano {room.floor})?
          La camera verra' soft-deleted (recuperabile via DB).
        </p>
        {errMsg && (
          <p role="alert" className="text-xs text-red-600">
            {errMsg}
          </p>
        )}
      </div>
      <ModalFooter
        onCancel={onCancel}
        onConfirm={onConfirm}
        loading={loading}
        confirmLabel="Elimina"
        danger
      />
    </ModalShell>
  );
}

// --------------------------- shared shell ---------------------------

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: overlay manuale
      role="dialog"
      aria-modal="true"
      data-testid="room-modal"
      className="fixed inset-0 z-[1000] flex items-center justify-center p-5"
      style={{ background: 'rgba(45, 36, 29, 0.5)' }}
    >
      <div
        className="bg-bg-card rounded-2xl overflow-hidden"
        style={{ width: 'min(440px, 96vw)', boxShadow: '0 24px 64px rgba(45,36,29,0.25)' }}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-border-base">
          <h3 className="text-base font-bold text-text-primary">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="w-8 h-8 rounded-lg bg-bg-card border border-border-base flex items-center justify-center text-text-secondary hover:bg-bg-base"
          >
            <X size={16} />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({
  onCancel,
  onConfirm,
  loading,
  confirmLabel = 'Salva',
  danger,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  confirmLabel?: string;
  danger?: boolean;
}) {
  return (
    <footer className="px-6 py-4 border-t border-border-base flex justify-end gap-2.5 bg-bg-base">
      <button
        type="button"
        onClick={onCancel}
        disabled={loading}
        data-testid="modal-cancel"
        className="bg-bg-card text-text-secondary border border-border-base px-5 py-2 rounded-lg text-[13px] font-semibold"
      >
        Annulla
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={loading}
        data-testid="modal-confirm"
        className="text-white border-0 px-5 py-2 rounded-lg text-[13px] font-semibold"
        style={{ background: danger ? '#A8453A' : '#E97B3D' }}
      >
        {loading ? '...' : confirmLabel}
      </button>
    </footer>
  );
}

function Field({
  label,
  required,
  value,
  onChange,
  placeholder,
  type,
  testId,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  testId?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] uppercase tracking-[0.5px] font-semibold text-text-secondary">
        {label}
        {required && <span className="text-accent-primary ml-0.5">*</span>}
      </span>
      <input
        type={type ?? 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="px-3.5 py-2.5 rounded-lg border border-border-input bg-bg-card text-sm text-text-primary"
      />
    </label>
  );
}

function SelectField({
  label,
  required,
  value,
  onChange,
  options,
  testId,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  testId?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] uppercase tracking-[0.5px] font-semibold text-text-secondary">
        {label}
        {required && <span className="text-accent-primary ml-0.5">*</span>}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="px-3.5 py-2.5 rounded-lg border border-border-input bg-bg-card text-sm text-text-primary"
      >
        <option value="">— seleziona —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'room_has_active_bookings') {
      return 'Camera ha booking attive. Cancellali prima di eliminare.';
    }
    if (err.code === 'room_code_taken') return 'Codice camera gia in uso.';
    return `Errore ${err.status}${err.code ? ` (${err.code})` : ''}`;
  }
  if (err instanceof Error) return err.message;
  return 'Errore sconosciuto';
}
